/**
 * VendorConversionWidgetsPanel — Iter 69.
 *
 * Lets vendors toggle the 3 conversion-boost widgets that appear on their
 * car-detail pages. All three default to ON. Disabling a widget hides it
 * for every car in this vendor's fleet — server-side `enrich_car` strips
 * the option, so even direct-link visits don't see it.
 *
 * Saves via `PATCH /api/users/me/business` — same endpoint used by every
 * other vendor-profile setting. Keeps the data model boring.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Save, TrendingUp, Sparkles, Repeat } from "lucide-react";
import { toast } from "sonner";

const ROWS = [
  {
    key: "stay_longer",
    field: "conversion_widget_stay_longer",
    icon: TrendingUp,
    accent: "text-amber-700 bg-amber-100 ring-amber-200",
    title: "Stay a little longer",
    desc: "Shows the renter how much extending their trip by 1, 2, or 3 days would cost. Unlocks any length-of-rental discount tier you've configured.",
  },
  {
    key: "book_earlier",
    field: "conversion_widget_book_earlier",
    icon: Sparkles,
    accent: "text-emerald-700 bg-emerald-100 ring-emerald-200",
    title: "Book earlier, save €X",
    desc: "Nudges peak-season bookings into shoulder season. Self-hides when no shifted window saves the renter money.",
  },
  {
    key: "similar_in_vendor",
    field: "conversion_widget_similar_in_vendor",
    icon: Repeat,
    accent: "text-teal-700 bg-teal-100 ring-teal-200",
    title: "Same fleet, save €X",
    desc: "Lists up to 3 other cars from your fleet that are cheaper for the same dates. Keeps the renter inside your funnel.",
  },
];

export default function VendorConversionWidgetsPanel() {
  const [form, setForm] = useState({
    conversion_widget_stay_longer:       true,
    conversion_widget_book_earlier:      true,
    conversion_widget_similar_in_vendor: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Iter 69 — 30-day click-through stats per widget.
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => {
        const u = r.data || {};
        setForm({
          // `!= false` so a brand-new vendor (field undefined) defaults to ON.
          conversion_widget_stay_longer:       u.conversion_widget_stay_longer       !== false,
          conversion_widget_book_earlier:      u.conversion_widget_book_earlier      !== false,
          conversion_widget_similar_in_vendor: u.conversion_widget_similar_in_vendor !== false,
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get("/vendor/conversion-widgets/stats?days=30")
      .then((r) => setStats(r.data?.stats || null))
      .catch(() => setStats(null));
  }, []);

  const onSave = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/business", form);
      toast.success("Conversion widget preferences saved.");
    } catch (err) {
      toast.error(formatApiError(err) || "Could not save preferences.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="vendor-conversion-widgets-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-indigo-600" /> Conversion widgets
        </CardTitle>
        <CardDescription>
          Small smart nudges shown to renters on your car-detail pages. Each
          can be turned off independently. All are ON by default — changes
          apply across your whole fleet within seconds of saving.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-neutral-400 text-sm gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-3">
            {ROWS.map((row) => {
              const Icon = row.icon;
              const s = stats?.[row.key];
              return (
                <label
                  key={row.key}
                  htmlFor={`conversion-widget-${row.key}`}
                  className="flex items-start gap-3 rounded-xl border border-neutral-200 hover:border-neutral-300 p-3 cursor-pointer"
                  data-testid={`conversion-widget-row-${row.key}`}
                >
                  <span className={`h-8 w-8 rounded-lg flex items-center justify-center ring-1 ${row.accent} shrink-0`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-neutral-900">{row.title}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">{row.desc}</div>
                    {s && (s.impressions || s.clicks) ? (
                      <div className="mt-2 flex items-center gap-3 text-[11px] font-mono text-neutral-700" data-testid={`conversion-widget-stats-${row.key}`}>
                        <span><strong>{s.impressions}</strong> impressions</span>
                        <span>·</span>
                        <span><strong>{s.clicks}</strong> clicks</span>
                        <span>·</span>
                        <span>CTR <strong>{s.ctr}%</strong></span>
                        <span>·</span>
                        <span className="text-emerald-700">{s.bookings_attributed} booking{s.bookings_attributed === 1 ? "" : "s"}</span>
                      </div>
                    ) : null}
                  </div>
                  <Switch
                    id={`conversion-widget-${row.key}`}
                    checked={form[row.field]}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, [row.field]: v }))}
                    data-testid={`conversion-widget-switch-${row.key}`}
                  />
                </label>
              );
            })}
            <div className="pt-2">
              <Button
                onClick={onSave}
                disabled={saving}
                className="rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700"
                data-testid="vendor-conversion-widgets-save"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save preferences
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
